'use client';
import {Box} from '@mui/material';
import {styled, type SxProps, type Theme} from '@mui/material/styles';
import {type FontFamily, type FontSize, type FontWeight, type LineHeight, Typography} from './Typography';

export interface SectionHeadingProps {
  title: string;
  subtitle?: string;
  titleSize?: FontSize;
  titleWeight?: FontWeight;
  titleMobileSize?: FontSize;
  titleMobileWeight?: FontWeight;
  subtitleSize?: FontSize;
  subtitleLineHeight?: LineHeight;
  subtitleMobileSize?: FontSize;
  subtitleFamily?: FontFamily;
  tone?: SectionHeadingTone;
  sx?: SxProps<Theme>;
}

export type SectionHeadingTone = 'default' | 'inverse';

const HEADING_TONE: Record<SectionHeadingTone, (theme: Theme) => {title: string; subtitle: string}> = {
  default: theme => ({title: theme.palette.colors.text.neutral.strong, subtitle: theme.palette.colors.text.neutral.default}),
  inverse: theme => ({title: theme.palette.colors.text.neutral.inverse, subtitle: theme.palette.colors.text.neutral.inverse}),
};

const Root = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  textAlign: 'center',
}));

export function SectionHeading({
  title,
  subtitle,
  titleSize = 'title-2',
  titleWeight = 'bold',
  titleMobileSize,
  titleMobileWeight,
  subtitleSize = 'body-1',
  subtitleLineHeight = '24',
  subtitleMobileSize,
  subtitleFamily,
  tone = 'default',
  sx,
}: SectionHeadingProps) {
  return (
    <Root sx={sx}>
      <Typography
        size={titleSize}
        weight={titleWeight}
        mobileSize={titleMobileSize}
        mobileWeight={titleMobileWeight}
        sx={{
          textTransform: 'capitalize',
          color: theme => HEADING_TONE[tone](theme).title,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          size={subtitleSize}
          lineHeight={subtitleLineHeight}
          mobileSize={subtitleMobileSize}
          family={subtitleFamily}
          sx={{
            color: theme => HEADING_TONE[tone](theme).subtitle,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Root>
  );
}

SectionHeading.displayName = 'SectionHeading';
